import apiClient from './apiConfig';
import { encryptUserData, decryptUserData, encryptUserDataAsync, decryptUserDataAsync } from './zkDataEncryption';
import { retrieveMasterKey } from './zkEncryption';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

/**
 * Zero-Knowledge Encrypted API Helper
 *
 * This module provides wrapper functions for all encrypted API endpoints.
 * It automatically handles client-side zero-knowledge encryption/decryption
 * using the master key derived from the user's password (PBKDF2 with 100k iterations).
 * The master key is stored in sessionStorage and never sent to the server.
 */

/**
 * Generic fetch function with zero-knowledge decryption
 */
const fetchEncrypted = async (endpoint, userEmail) => {
  try {
    const masterKey = retrieveMasterKey();
    if (!masterKey) {
      throw new Error('Master key not found. Please log in again.');
    }

    const response = await apiClient.get(`${API}/${endpoint}`);

    // Decrypt all records in parallel via the native Web Crypto fast path.
    // crypto.subtle runs off the JS main thread, so N records no longer block
    // the UI the way N sequential CryptoJS PBKDF2 calls did.
    const decryptedItems = await Promise.all(
      response.data.map(async (item) => {
        try {
          const decrypted = await decryptUserDataAsync(item.encryptedPayload, true);
          return {
            id: item.id,
            ...decrypted,
            visibilitySettings: item.visibilitySettings,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt
          };
        } catch (error) {
          return null;
        }
      })
    );
    return decryptedItems.filter(Boolean);
  } catch (error) {
    if (error.message && error.message.includes('Master key not found')) {
      throw new Error('Session expired. Please log in again.');
    }
    throw error;
  }
};

/**
 * Generic create function with zero-knowledge encryption
 */
const createEncrypted = async (endpoint, data, visibilitySettings, userEmail) => {
  try {
    const masterKey = retrieveMasterKey();
    if (!masterKey) {
      throw new Error('Master key not found. Please log in again.');
    }

    const encryptedPayload = await encryptUserDataAsync(data);

    return await apiClient.post(`${API}/${endpoint}`, {
      encryptedPayload,
      visibilitySettings: visibilitySettings || { visibilityType: "all", selectedNominees: [] }
    });
  } catch (error) {
    if (error.message && error.message.includes('Master key not found')) {
      throw new Error('Session expired. Please log in again.');
    }
    throw error;
  }
};

/**
 * Generic update function with zero-knowledge encryption
 */
const updateEncrypted = async (endpoint, id, data, visibilitySettings, userEmail) => {
  try {
    const masterKey = retrieveMasterKey();
    if (!masterKey) {
      throw new Error('Master key not found. Please log in again.');
    }

    const encryptedPayload = await encryptUserDataAsync(data);

    return await apiClient.put(`${API}/${endpoint}/${id}`, {
      encryptedPayload,
      visibilitySettings: visibilitySettings || { visibilityType: "all", selectedNominees: [] }
    });
  } catch (error) {
    if (error.message && error.message.includes('Master key not found')) {
      throw new Error('Session expired. Please log in again.');
    }
    throw error;
  }
};

/**
 * Generic delete function (no encryption needed)
 */
const deleteItem = async (endpoint, id) => {
  return await apiClient.delete(`${API}/${endpoint}/${id}`);
};

// ==================== CREDENTIALS ====================

const validateCredentialsURL = (url) => {
  if (!url || url.trim() === '') {
    // URL is optional
    return true;
  }

  // URL must contain at least one dot (.)
  if (!url.includes('.')) {
    throw new Error('URL must contain a dot (.) - e.g., example.com, website.co.uk');
  }

  return true;
};

export const credentialsAPI = {
  fetch: (userEmail) => fetchEncrypted('credentials', userEmail),

  create: (data, visibilitySettings, userEmail) => {
    validateCredentialsURL(data.url);
    return createEncrypted('credentials', data, visibilitySettings, userEmail);
  },

  update: (id, data, visibilitySettings, userEmail) => {
    validateCredentialsURL(data.url);
    return updateEncrypted('credentials', id, data, visibilitySettings, userEmail);
  },

  delete: (id) => deleteItem('credentials', id)
};

// ==================== CARDS ====================

export const cardsAPI = {
  fetch: (userEmail) => fetchEncrypted('cards', userEmail),

  create: (data, visibilitySettings, userEmail) =>
    createEncrypted('cards', data, visibilitySettings, userEmail),

  update: (id, data, visibilitySettings, userEmail) =>
    updateEncrypted('cards', id, data, visibilitySettings, userEmail),

  delete: (id) => deleteItem('cards', id)
};

// ==================== FINANCIAL ====================

export const financialAPI = {
  fetch: (userEmail) => fetchEncrypted('financial', userEmail),

  create: async (data, visibilitySettings, _userEmail) => {
    const masterKey = retrieveMasterKey();
    if (!masterKey) throw new Error('Master key not found. Please log in again.');
    const encryptedPayload = await encryptUserDataAsync(data);
    return apiClient.post(`${API}/financial`, {
      encryptedPayload,
      visibilitySettings: visibilitySettings || { visibilityType: 'all', selectedNominees: [] },
      type: data.type || null,
    });
  },

  update: async (id, data, visibilitySettings, _userEmail) => {
    const masterKey = retrieveMasterKey();
    if (!masterKey) throw new Error('Master key not found. Please log in again.');
    const encryptedPayload = await encryptUserDataAsync(data);
    return apiClient.put(`${API}/financial/${id}`, {
      encryptedPayload,
      visibilitySettings: visibilitySettings || { visibilityType: 'all', selectedNominees: [] },
      type: data.type || null,
    });
  },

  delete: (id) => deleteItem('financial', id)
};

// ==================== DOCUMENTS ====================

export const documentsAPI = {
  fetch: (userEmail) => fetchEncrypted('documents', userEmail),

  create: (data, visibilitySettings, userEmail) =>
    createEncrypted('documents', data, visibilitySettings, userEmail),

  update: (id, data, visibilitySettings, userEmail) =>
    updateEncrypted('documents', id, data, visibilitySettings, userEmail),

  delete: (id) => deleteItem('documents', id)
};

// ==================== POLICIES ====================

export const policiesAPI = {
  fetch: (userEmail) => fetchEncrypted('policies', userEmail),

  create: (data, visibilitySettings, userEmail) =>
    createEncrypted('policies', data, visibilitySettings, userEmail),

  update: (id, data, visibilitySettings, userEmail) =>
    updateEncrypted('policies', id, data, visibilitySettings, userEmail),

  delete: (id) => deleteItem('policies', id)
};

// ==================== NOTES ====================

export const notesAPI = {
  fetch: (userEmail) => fetchEncrypted('notes', userEmail),

  create: (data, visibilitySettings, userEmail) =>
    createEncrypted('notes', data, visibilitySettings, userEmail),

  update: (id, data, visibilitySettings, userEmail) =>
    updateEncrypted('notes', id, data, visibilitySettings, userEmail),

  delete: (id) => deleteItem('notes', id)
};

// ==================== DASHBOARD STATS ====================

export const dashboardAPI = {
  /**
   * Fetch dashboard stats (counts only).
   * Uses the server-side COUNT(*) endpoint — no payloads are transferred and
   * nothing is decrypted. (Counting via full fetch+decrypt of every collection
   * would needlessly decrypt the entire vault just to read array lengths.)
   */
  fetchStats: async () => {
    const response = await apiClient.get(`${API}/dashboard/stats`);
    return response.data;
  }
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Validate zero-knowledge encryption setup
 */
export const validateEncryptionSetup = () => {
  try {
    const masterKey = retrieveMasterKey();
    if (!masterKey) {
      return {
        success: false,
        message: 'Master key not found',
        details: 'Please log in to derive your master key from your password'
      };
    }
    return { success: true, message: 'Zero-knowledge encryption configured correctly' };
  } catch (error) {
    return {
      success: false,
      message: error.message,
      details: 'Failed to retrieve master key from sessionStorage'
    };
  }
};

/**
 * Test zero-knowledge encryption/decryption
 */
export const testEncryption = () => {
  try {
    const masterKey = retrieveMasterKey();
    if (!masterKey) {
      return {
        success: false,
        message: 'Master key not found',
        error: 'Please log in again'
      };
    }

    const testData = { test: 'data', timestamp: new Date().toISOString() };

    const encrypted = encryptUserData(testData);
    const decrypted = decryptUserData(encrypted, true);

    const match = JSON.stringify(testData) === JSON.stringify(decrypted);

    return {
      success: match,
      message: match ? 'Zero-knowledge encryption test passed' : 'Zero-knowledge encryption test failed',
      original: testData,
      encrypted: JSON.stringify(encrypted).substring(0, 50) + '...',
      decrypted
    };
  } catch (error) {
    return {
      success: false,
      message: 'Zero-knowledge encryption test failed',
      error: error.message
    };
  }
};
